import React from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import TeamService from "../../services/TeamService";
import ClientsService from "../../services/ClientsService";
import {
  CoachBriefInfoDto,
  PaginatedListOfClientInfoDto,
} from "@growth-machine-llc/stridist-api-client";
import { usePopupState } from "material-ui-popup-state/hooks";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import { useOptimisticUpdateMutation } from "../../hooks/useOptimisticUpdateMutation";
import { COACH_CLIENTS_LIST_QUERY_KEY } from "./CoachClientsListScreen";
import { TEAM_COACHES_QUERY_KEY } from "../../routes/coach/team/CoachTeamRoute";
import { ArrowRight } from "lucide-react";

export interface AssignedCoachAvatarProps {
  clientId: number;
  clientName: string;
  clientPhotoUrl: string;
  assignedCoach?: { photoUrl?: string; displayName?: string };
  disableClientTransfer?: boolean;
}

export function AssignedCoachAvatar(props: AssignedCoachAvatarProps) {
  const {
    clientId,
    clientName,
    clientPhotoUrl,
    assignedCoach,
    disableClientTransfer,
  } = props;
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const queryClient = useQueryClient();

  const [selectedCoach, setSelectedCoach] = React.useState<{
    id: number;
    name: string;
    photoUrl: string | null;
  } | null>(null);

  const { data: coaches, isLoading } = useQuery<
    CoachBriefInfoDto[] | undefined
  >({
    queryKey: [TEAM_COACHES_QUERY_KEY],
    queryFn: () => TeamService.getCoaches(),
    enabled: open,
  });

  const confirmTransferClientDialogState = usePopupState({
    variant: "popover",
    popupId: "transfer-client",
  });

  const { mutate: transferClient, isPending } = useOptimisticUpdateMutation({
    queryKey: [COACH_CLIENTS_LIST_QUERY_KEY],
    mutationFn: (targetCoachId: number) =>
      ClientsService.transferClients({
        targetCoachId,
        clientId,
      }),
    exactQueryKey: false,
    successToastMessage: "Client reassigned.",
    optimisticUpdater: {
      updateFn: (
        oldData: PaginatedListOfClientInfoDto,
        targetCoachId: number,
        tempId: number,
      ): PaginatedListOfClientInfoDto => {
        if (!oldData?.items) return oldData;
        return {
          ...oldData,
          items: oldData.items.map((client) =>
            client.id === clientId
              ? { ...client, assignedCoachId: targetCoachId }
              : client,
          ),
        } as PaginatedListOfClientInfoDto;
      },
    },
    options: {
      onSuccess: async () => {
        setAnchorEl(null);
        confirmTransferClientDialogState.close();
        await queryClient.invalidateQueries({
          queryKey: [TEAM_COACHES_QUERY_KEY],
        });
        await queryClient.invalidateQueries({
          queryKey: [COACH_CLIENTS_LIST_QUERY_KEY],
        });
      },
    },
  });

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (disableClientTransfer) {
      return;
    }
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e?: unknown) => {
    if (e && (e as any).stopPropagation) {
      (e as any).stopPropagation();
    }
    setAnchorEl(null);
  };

  const getFirstLetter = (name: string | null) =>
    (name || "?").toString().charAt(0).toUpperCase();
  const assignedCoachFirstLetter = getFirstLetter(assignedCoach?.displayName);

  return (
    <>
      <Avatar src={assignedCoach?.photoUrl} onClick={handleOpen}>
        {assignedCoachFirstLetter}
      </Avatar>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", p: 1.5 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          (coaches || []).map((coach) => {
            const isCurrent =
              assignedCoach?.displayName &&
              coach.displayName === assignedCoach.displayName;
            return (
              <>
                <MenuItem
                  key={coach.id as number}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAnchorEl(null);
                    setSelectedCoach({
                      id: coach.id,
                      name: coach.displayName ?? coach.username,
                      photoUrl: coach.photoUrl,
                    });
                    confirmTransferClientDialogState.open();
                  }}
                  disabled={isPending}
                >
                  <ListItemAvatar>
                    <Avatar src={coach.photoUrl}>
                      {(coach.displayName || "?").charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={coach.displayName}
                    secondary={coach.email}
                  />
                  {isCurrent && <DoneIcon color="primary" fontSize="small" />}
                </MenuItem>
              </>
            );
          })
        )}
      </Menu>

      {confirmTransferClientDialogState.isOpen && selectedCoach?.id && (
        <Box
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            cursor: "auto",
          }}
        >
          <ConfirmActionDialog
            title={`Transfer ${clientName}?`}
            description={
              <Stack gap={3}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <Avatar src={clientPhotoUrl} sx={{ width: 80, height: 80 }}>
                      {getFirstLetter(clientName)}
                    </Avatar>
                    <ArrowRight size={40} />
                    <Avatar
                      src={selectedCoach.photoUrl}
                      sx={{ width: 80, height: 80 }}
                    >
                      {getFirstLetter(selectedCoach.name)}
                    </Avatar>
                  </Box>
                </Box>
                <Typography variant="body2">
                  This can take up to a few minutes. Please don't leave this
                  screen while client data is transferred to{" "}
                  {selectedCoach.name}.
                </Typography>
              </Stack>
            }
            open={confirmTransferClientDialogState.isOpen}
            onCancel={confirmTransferClientDialogState.close}
            onConfirm={() => {
              transferClient(selectedCoach.id);
            }}
            disabled={isPending}
            confirmButtonText="Transfer"
          />
        </Box>
      )}
    </>
  );
}

export default AssignedCoachAvatar;
